import * as LogRocket from 'logrocket';
import { CUSTOM_FRONTEND_BASEURLS, DEFAULT_ORG_PRETTY_NAME } from 'src/globals';
import { RECRONRELIANCE_ORGS } from 'src/Library/Constants';

declare let window: any;
let recordingURL: any = null;

export const getAuthenticationHeaders = () => {
  const storage = window.localStorage;
  if (!recordingURL) {
    LogRocket.getSessionURL((sessionURL) => {
      recordingURL = sessionURL;
    });
  }
  return {
    'user-id': storage.getItem('userId'),
    'access-token': storage.getItem('authToken'),
    'organisation-id': storage.getItem('organisationId'),
    'X-LogRocket-URL': recordingURL,
    'user-location': getClientDeviceLocation()
  };
};

// get client device longitude and latitude via browserAPI
export const getClientDeviceLocation = () => {
  return window.localStorage.getItem('userLocation');
}

export const storeClientDeviceLocation = () => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        window.localStorage.setItem('userLocation', 
          `Location:${position.coords.latitude}:${position.coords.longitude}`
        )
      }, 
      (error) => {
        let errorStr;
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorStr = 'User denied the request for Geolocation.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorStr = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorStr = 'The request to get user location timed out.';
            break;
          default:
            errorStr = 'An unknown error occurred.';
        }
        window.localStorage.setItem('userLocation', `Error:${errorStr}`);
      });
  } else {
    window.localStorage.setItem('userLocation', `Error:Geolocation is not supported by this browser.`);
  }
}

export const getOrgPrettyName = () => {
  if (
    window.location.hostname.indexOf('.') > 0 &&
    CUSTOM_FRONTEND_BASEURLS &&
    CUSTOM_FRONTEND_BASEURLS.length &&
    CUSTOM_FRONTEND_BASEURLS.includes(
      window.location.hostname.substring(window.location.hostname.indexOf('.') + 1)
    )
  ) {
    const temp = window.location.hostname.substring(0, window.location.hostname.indexOf('.'));
    const x = temp.indexOf('-');
    return temp.substring(0, x);
  }
  if (window.location.hostname.indexOf('.') > 0) {
    return window.location.hostname.substring(0, window.location.hostname.indexOf('.'));
  }
  return DEFAULT_ORG_PRETTY_NAME;
};

export const getErrorMessage = (err) => {
  let errorMessage = '';
  if (!err.response) {
    errorMessage = 'Could not connect. Please check your internet connection';
  } else {
    const responseData = err.response.data;
    if (responseData && responseData.error) {
      if (typeof responseData.error === 'object') {
        // Old format: { error: { message: "..." } }
        errorMessage =
          Object.prototype.toString.call(responseData.error.message) === '[object Object]'
            ? responseData.error.message.message
            : responseData.error.message;
      } else {
        // NestJS format: { statusCode, message: string | string[], error: "Bad Request" }
        errorMessage = Array.isArray(responseData.message)
          ? responseData.message.join(', ')
          : responseData.message || responseData.error;
      }
    } else {
      errorMessage = `Something went wrong\n${err.message}`;
    }
  }
  return errorMessage;
};

export const transformChartFormDataToApiParams = (inpFormData) => {
  // Ensure immutability on input data
  const formData = { ...inpFormData };
  const updatedParams: any = {};
  const fieldsToMoveOut = ['linkTo'];
  for (const paramKey in formData) {
    if (fieldsToMoveOut.indexOf(paramKey) > -1) {
      updatedParams[paramKey] = formData[paramKey];
      delete formData[paramKey];
    }
  }
  updatedParams.formData = formData;
  return updatedParams;
};

export const transformApiParamsToChartFormData = (inpParams) => {
  // Ensure immutability of input data
  const params = { ...inpParams };
  const newFormData = { ...params.formData };
  const fieldsToMoveIn = ['linkTo'];
  for (const paramKey in params) {
    if (fieldsToMoveIn.indexOf(paramKey) > -1) {
      newFormData[paramKey] = params[paramKey];
      delete params[paramKey];
    }
  }
  params.formData = newFormData;

  return params;
};

export const generateQueryString = (paramsArray) => {
  const queryString = paramsArray
    ?.map(({ key, val }) => {
      if (val !== undefined && val !== null) {
        return `${key}=${encodeURIComponent(val)}`;
      }
      return null;
    })
    .filter((elem: any) => elem !== null)
    .join('&');
  return queryString;
};

export const generateObjectFromList = (key, list) => {
  const generatedList: any = [];
  if (list) {
    list.forEach((element) => {
      generatedList.push({ key, val: element });
    });
  }
  return generatedList;
};

export const getErrorMessageFromFailureArraySingle = (failureArray) => {
  const unknownError = 'Some error occurred';
  if (
    failureArray &&
    failureArray.length &&
    failureArray[0] &&
    failureArray[0].reasons &&
    failureArray[0].reasons.length
  ) {
    return failureArray[0].reasons[0].message || unknownError;
  }
  return unknownError;
};

export const getErrorTableDataFromFailureArrayMultiple = (failureArray, key, extraDetailKey) => {
  const tableData: any = [];
  if (failureArray && failureArray.length) {
    failureArray.forEach((failureElem: any) => {
      const obj: any = {};
      obj[key] = failureElem.extraDetails[extraDetailKey][key];
      obj.errorMessage =
        failureElem.reasons && failureElem.reasons.length
          ? failureElem.reasons.map((elem: any) => elem.message).join(', ')
          : [];
      tableData.push(obj);
    });
  }
  return tableData;
};

export const isOrgReliance = () => {
  const orgId = window.localStorage.getItem('organisationId');
  if (orgId) {
    return ['reliance', 'reliancetest'].indexOf(orgId) > -1;
  }
  return false;
};
export const isOrgRecronreliance = () => {
  const orgId = window.localStorage.getItem('organisationId');
  if (orgId) {
    return RECRONRELIANCE_ORGS.indexOf(orgId) > -1;
  }
  return false;
};
export const checkForEquality = (newVal, oldVal) => {
  if (newVal instanceof Array) {
    return JSON.stringify(newVal) === JSON.stringify(oldVal);
  }
  return newVal === oldVal;
};

export const getChangesInData = <
  N extends Record<string, any>,
  O extends Record<string, any>
>(newObj: N, oldObj: O): Partial<N | O> => {
  const returnObject: Record<string, any> = {};
  Object.keys(newObj).map((item) => {
    if (newObj[item] instanceof Object && !(newObj[item] instanceof Array)) {
      if (!oldObj[item]) {
        returnObject[item] = newObj[item];
      } else {
        const changedObj = getChangesInData(newObj[item], oldObj[item]);
        if (Object.keys(changedObj).length > 0) {
          returnObject[item] = changedObj;
        }
      }
    } else {
      if (!checkForEquality(newObj[item], oldObj[item])) {
        returnObject[item] = newObj[item];
      }
    }
  });
  return returnObject as Partial<N | O>;
};

export const getErrorMessageForBlob = async (err) => {
  let errorMessage = '';
  if (!err.response) {
    errorMessage = 'Could not connect. Please check your internet connection';
  } else {
    const responseData = await new Response(err.response.data).json();
    if (responseData && responseData.error) {
      errorMessage = responseData.error.message;
    } else {
      errorMessage = `Something went wrong\n${err.message}`;
    }
  }
  return errorMessage;
};

export const editEnableInOrgProfile = (linkedOrgId, role) => {
  if (role !== 'ADMIN') return false;
  return linkedOrgId === window.localStorage.getItem('organisationId');
};

export const toArray = (val?: any, valueToReturnIfUndefined: any = undefined) => {
  return val ? [val] : valueToReturnIfUndefined;
};

export const strListToKeyLabelList = (list?: string[]) => {
  return list?.map((el) => ({ key: el, label: el })) || [];
};

export const strListToKeyLabelListCB = (list: string[]) => {
  return strListToKeyLabelList(list);
};

export const mockResponse = ({
  data = window.mock,
  isSuccess = true,
}: {
  data?: any;
  isSuccess?: boolean;
}) => {
  return new Promise((resolve) => resolve({ data: data as any, isSuccess }));
};

export const isOrgMothersonGroup = (shipperId?: string) => {
  const allowedOrgId = ['mothersongroup', 'mothersongrouptest'];
  if (shipperId) {
    return allowedOrgId.includes(shipperId);
  } else {
    const orgId = window.localStorage.getItem('organisationId');
    if (orgId) {
      return allowedOrgId.indexOf(orgId) > -1;
    }
  }
  return false;
};


